import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { token } from '@bp-files/ui';

/**
 * The card grid, and the card in it — Files' one navigational shape.
 *
 * It is used twice, at two levels of the same navigation: agencies at `/files`
 * (`AgencyGrid`) and folders at a space root (`SpaceRoot`). That is the reason
 * it lives here rather than staying private to `SpaceRoot`, where it was born.
 * Drilling in reads as one gesture repeated, and the visual treatment below is
 * long enough that a second copy of it would be a second copy of the *reasons*
 * — which is the failure ADR-0031 named.
 *
 * **It is a shape, not a data type.** The card knows about a title, a line
 * under it, and a metadata slot; it knows nothing about folders or spaces.
 * `meta` is a `ReactNode` for exactly that reason: `MetaRow` from
 * `@bp-files/ui` already owns the amber-for-time rule, each caller owns its own
 * `dateLocale`, and an agency that has never been used needs a fallback line a
 * folder never does.
 *
 * This module is not in `@bp-files/ui`, and cannot be: the card is a
 * `react-router` `<Link>`, and the design system takes no router dependency.
 */

/**
 * The card at rest — radius, border, fill, padding — with no interaction.
 *
 * Exported for a card-shaped **form** sitting in the same grid (`CreateFolder`),
 * which needs the surface without the hover: it is already open, so there is
 * nothing for a hover cue to promise.
 *
 * **The fill is `elevated`, not `card` — one step down the same ladder the
 * active sidebar item uses.** `card` (`#1e293b`) reads as the lightest surface
 * on this flat palette; against the blue border it looked washed out rather
 * than raised.
 *
 * **The resting border is `hairline` (`#242d3d`), barely a shade off the
 * card's own fill.** It used to be `accent-hover` (`#3a7bc2`) — the full
 * accent one step down — and a whole grid outlined in blue read as louder
 * than a resting surface should: the readme's "signal" colour, worn as
 * furniture. A tile now needs the hover below to say anything blue at all.
 *
 * Surface and border are Tailwind theme classes rather than inline `token()`
 * styles, and that is what makes the hover below possible at all: an inline
 * style wins over a `hover:` utility, so a card styled inline can only ever
 * have one appearance. The classes resolve to the same palette values
 * (`index.css`'s `@theme` block).
 */
export const CARD_SURFACE_CLASS =
  'flex h-full flex-col gap-2 rounded-lg border border-hairline bg-elevated p-4';

/**
 * The same surface, plus the interaction.
 *
 * Interaction reaches full `accent-bright` (`#5da0eb`) on hover and focus, with
 * the design system's soft accent glow behind it (`--shadow-accent-soft`, its
 * `glow-blue-soft`) — from a resting `hairline` border, blue appears from
 * nowhere rather than merely brightening, which is what makes it read as the
 * cue. At rest the card is shadowless, which is the system's rule; the glow
 * is the hover.
 *
 * `transition-[color,background-color,border-color,box-shadow]` rather than
 * `transition-colors`, which does not carry `box-shadow` — without it the
 * border eases and the glow snaps.
 *
 * `focus-visible` gets the same treatment, so the cue is not reserved for
 * people using a mouse.
 */
const CARD_INTERACTIVE_CLASS = `${CARD_SURFACE_CLASS} transition-[color,background-color,border-color,box-shadow] hover:border-accent-bright hover:shadow-accent-soft focus-visible:border-accent-bright focus-visible:shadow-accent-soft`;

/**
 * The grid itself. Callers supply the `<li>`s, so a caller can put something
 * that is not a `NavCard` in one — `SpaceRoot` opens its create form as the
 * first item.
 */
export function CardGrid({ children, label }: { children: ReactNode; label?: string }) {
  return (
    <ul aria-label={label} className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {children}
    </ul>
  );
}

/**
 * The tile's type scale — **one size, and a tile may use no other.**
 *
 * Files renders three kinds of tile in one grid, and before this they carried
 * four steps between them: an agency and a folder titled at `text-sm` over
 * `text-xs` bodies, a file titled at `text-lg` with a `text-sm` size line over
 * the same `text-xs` body. Scanning the three levels of one navigation meant
 * re-reading what counted as a title at each. **Weight and colour carry the
 * hierarchy now, not size** — `font-semibold` in `bp-text` is a title,
 * ordinary weight in `bp-text-secondary` is everything under it, both at the
 * one size a denser tile needs to fit its rows.
 *
 * The date is **not** a third step. It is the body's weight wearing `bp-amber`
 * — `MetaRow`'s `size="sm"`, which now matches title and body alike. Colour is
 * what marks it as time in this system, so it is the only thing that has to
 * differ.
 */
export const CARD_TITLE_CLASS = 'text-sm font-semibold';
export const CARD_BODY_CLASS = 'text-sm';

export interface CardBodyProps {
  /**
   * The glyph, which **owns the left column** rather than sitting on the
   * title's baseline. At `size-6`, top-aligned with the title in its own
   * gutter, it is the thing that says what kind of tile this is before any of
   * the text is read, which is what a grid of thirty tiles is scanned for.
   *
   * Optional, and deliberately a slot rather than a widened `title`: a caller
   * with nothing to show passes nothing, which is exactly what a counterpart's
   * `agencyType` being absent means (the field predates most fixtures in this
   * workspace, so the wire shape treats it as optional).
   */
  icon?: ReactNode;
  /**
   * Under the icon, centred on it — `FolderView`'s verification dot. It is a
   * fact about the file rather than a line of its description, so it reads as
   * one badge with the kind glyph instead of competing with the body text.
   */
  iconFooter?: ReactNode;
  /** The title line. A `ReactNode`, so a file can title itself with a button. */
  title: ReactNode;
  /** The line or lines under it — a folder's file count, an agency's domain. */
  detail: ReactNode;
  /** The last line: a `<MetaRow>`, or whatever stands in for one. */
  meta: ReactNode;
  /** Pinned to the card's top-right — `FolderView`'s row overflow. */
  trailing?: ReactNode;
}

/**
 * The card's inside: an icon column, then everything else.
 *
 * Separate from `NavCard` because a file card is not navigable — a file has no
 * address to space to, its name opens a viewer or spends a minutes-lived SAS —
 * and yet it has to be the *same tile*. Sharing the surface class alone was not
 * enough for that; the three tiles drifted anyway, because the shape lived in
 * three files.
 *
 * `items-start` on the row keeps the icon column pinned to the title's own
 * top edge rather than stretched to the tile's full height — the glyph sits
 * where the eye lands first, beside the fact it names, instead of centred in
 * a column that used to run the whole card regardless of how many rows sat
 * beside it.
 */
export function CardBody({ icon, iconFooter, title, detail, meta, trailing }: CardBodyProps) {
  return (
    <div className="flex flex-1 items-start gap-3">
      {icon === undefined && iconFooter === undefined ? null : (
        <div className="flex w-8 shrink-0 flex-col items-center gap-1">
          {/*
            The upscale is scoped to this wrapper, not to the column, and that
            is not tidiness: `iconFooter` is `FolderView`'s verification dot,
            which grows its own `<svg>` for 1600ms after a hash is copied. A
            column-wide `[&_svg]` would catch that check glyph and blow it to
            the kind icon's size mid-animation.

            Sized here rather than on each glyph because every one of them —
            `FolderIcon`, `AgencyTypeIcon`, `FileKindIcon` — also renders in a
            table row, at the row's size, from the same component.
          */}
          {icon === undefined ? null : <span className="flex [&_svg]:size-6">{icon}</span>}
          {iconFooter}
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-start justify-between gap-2">
          <div className={`min-w-0 ${CARD_TITLE_CLASS}`} style={{ color: token('bp-text') }}>
            {title}
          </div>
          {trailing}
        </div>

        <div className={CARD_BODY_CLASS} style={{ color: token('bp-text-secondary') }}>
          {detail}
        </div>

        {meta}
      </div>
    </div>
  );
}

export interface NavCardProps extends Omit<CardBodyProps, 'title' | 'detail'> {
  /** Where the card goes. Already encoded by the caller. */
  to: string;
  /** The title line — a folder's name, a space's name. */
  name: string;
  /**
   * The line under it — a folder's file count, a space's member chips.
   *
   * **A `ReactNode`, matching `CardBodyProps`' own slot.** It was `string`,
   * which was true while the line was one fact rendered as text; FIL-1's roster
   * is a bounded set of chips with agency-type glyphs, and narrowing here only
   * ever forced a caller to reach past `NavCard` for `CardBody`.
   */
  detail: ReactNode;
}

/**
 * One card, and the way one level further in.
 *
 * A link rather than a click handler, because every level of this navigation is
 * an address (ADR-0007) — which is what makes CON-15's copied space link work
 * at all.
 *
 * **There is no `actions` slot any more, and the reason it existed is worth
 * keeping.** `SpaceRoot`'s folder overflow was its one caller, and it needed the
 * slot because the whole card is an `<a>` and a `<button>` inside an anchor is
 * invalid HTML — the parser may hoist it out, and a click on it navigates as
 * well as fires. So the control rendered as a *sibling* of the link, absolutely
 * positioned over its top-right inside a `relative` **block** wrapper (block,
 * not flex, or the card would size to its content and the trigger would land in
 * the grid's gutter), with `pr-10` on the link reserving room.
 *
 * That folder overflow is gone — rename and delete live on the folder's own
 * page. **The card has one destination again, which is what it is for.** If a
 * second control is ever wanted on a tile, the constraint above is the one to
 * re-derive, and it has not changed.
 */
export function NavCard({ to, name, detail, ...body }: NavCardProps) {
  return (
    <Link to={to} className={CARD_INTERACTIVE_CLASS}>
      <CardBody {...body} title={name} detail={detail} />
    </Link>
  );
}
